import { useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MACHINES } from "@/data/site";
import { MachineCard } from "@/components/machines/MachineCard";
import { SectionHead } from "@/components/motion/Reveal";

const useDesktop = () => {
  const [d, setD] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setD(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return d;
};

const Head = ({ index }) => (
  <SectionHead index={index} eyebrow="Equipment Showcase" title={["Machines ready", "for your location."]}>
    <p>A look at popular equipment Brewer places and services. Request any machine and we'll match it to your space.</p>
    <Link to="/equipment" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-volt" data-testid="showcase-view-all-link">
      View all equipment <ArrowRight className="h-4 w-4" />
    </Link>
  </SectionHead>
);

const Pinned = ({ index }) => {
  const ref = useRef(null);
  const track = useRef(null);
  const [dist, setDist] = useState(0);
  useLayoutEffect(() => {
    const measure = () => setDist(Math.max(0, track.current.scrollWidth - window.innerWidth));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);
  return (
    <section ref={ref} style={{ height: `calc(100vh + ${dist}px)` }} className="relative border-t border-white/5 bg-ink-900" data-testid="equipment-showcase-section">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden pt-16">
        <div className="container-x"><Head index={index} /></div>
        <motion.div ref={track} style={{ x }} className="mt-10 flex w-max gap-6 px-12">
          {MACHINES.map((m) => (
            <MachineCard key={m.id} m={m} className="w-[400px] shrink-0" />
          ))}
        </motion.div>
        <div className="container-x mt-8">
          <div className="h-px w-full bg-white/10">
            <motion.div style={{ scaleX: scrollYProgress }} className="h-px origin-left bg-volt" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Swipe = ({ index }) => (
  <section className="relative border-t border-white/5 bg-ink-900 py-24" data-testid="equipment-showcase-section">
    <div className="container-x"><Head index={index} /></div>
    <div className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 sm:px-8" data-lenis-prevent-wheel>
      {MACHINES.map((m) => (
        <MachineCard key={m.id} m={m} className="w-[85vw] max-w-[380px] shrink-0 snap-start" />
      ))}
    </div>
  </section>
);

export const Showcase = ({ index = "04" }) => (useDesktop() ? <Pinned index={index} /> : <Swipe index={index} />);
